import { type EntryPoint, type EntryPointComponent, type PreloadFetchPolicy } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import type { TeamProfileCardContextValues } from '../store/context';
import type { TeamProfileCardRelay_AtlassianTeamProfilecard$key } from '../ui/__generated__/TeamProfileCardRelay_AtlassianTeamProfilecard.graphql';
import type { AvatarProps } from '../ui/components/Avatar';
import type { HeaderImageProps } from '../ui/components/HeaderImage';
import type { MemberListProps } from '../ui/components/MemberList';
import type { MoreActionsProps } from '../ui/components/MoreActions';
import type { TeamDescriptionProps } from '../ui/components/TeamDescription';
import type { TeamNameProps } from '../ui/components/TeamName';

import parameters, {
	type TeamProfileCardEntryPointQuery,
	type TeamProfileCardEntryPointQuery$variables,
} from './__generated__/TeamProfileCardEntryPointQuery.graphql';

const fetchPolicyDEPRECATED: PreloadFetchPolicy = 'network-only';
const fetchPolicy: PreloadFetchPolicy = 'store-or-network';

const entryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryRef: TeamProfileCardEntryPointQuery;
		},
		Record<string, never>,
		Omit<
			Pick<
				{
					teamAri: TeamProfileCardContextValues['teamAri'];
					cloudId: TeamProfileCardContextValues['cloudId'];
					containerId?: TeamProfileCardContextValues['containerId'];
					userId: TeamProfileCardContextValues['userId'];
					showFlag?: TeamProfileCardContextValues['showFlag'];
					headerImageUrl?: HeaderImageProps['src'];
					avatarSrc?: AvatarProps['src'];
					hasMoreMembers?: boolean;
					teamName: TeamNameProps['teamName'];
					isVerified?: TeamNameProps['isVerified'];
					isArchived?: boolean;
					members: MemberListProps['members'];
					description?: TeamDescriptionProps['description'];
					additionalActions?: MoreActionsProps['additionalActions'];
					townsquareApp?: TeamProfileCardContextValues['townsquareApp'];
				},
				'cloudId' | 'containerId' | 'userId' | 'showFlag' | 'additionalActions' | 'townsquareApp'
			> & {
				teamFragment: TeamProfileCardRelay_AtlassianTeamProfilecard$key;
			},
			'teamFragment'
		>,
		Record<string, never>
	>,
	TeamProfileCardEntryPointQuery$variables
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_team-profile-card-entrypoint" */ './TeamProfileCardEntryPoint'
			),
		{
			moduleId: 'team-profile-card-entrypoint',
		},
	),
	getPreloadProps: ({ cloudId, teamId }: TeamProfileCardEntryPointQuery$variables) => ({
		queries: {
			queryRef: {
				parameters,
				variables: { cloudId, teamId },
				options: {
					fetchPolicy: fg('ptc-fix-rate-limiting-on-profile-card')
						? fetchPolicy
						: fetchPolicyDEPRECATED,
				},
			},
		},
	}),
});

export { entryPoint };
